import { log as debugLog } from '@/utils/logger'
import { toUtcSeconds } from '@/utils/utcTime'
import { mtfSuffix, tfSeconds } from '@/utils/mtf'
import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'
import { BoxFillSeries } from '@/indicators/ict_quantum/BoxFillSeries'
import { LabelOverlaySeries } from '@/indicators/ict_quantum/LabelOverlaySeries'

// ─── Helpers de tiempo y validación ───────────────────────────────────────────

// Conversor ÚNICO compartido (@/utils/utcTime, v1.3.2): acepta segundos,
// milisegundos (payloads /charting/*), strings 'YYYY-MM-DD' (TFs diarios+) y
// business-day objects de LWC. Antes cleanTime solo convertía ms→s de números
// y los times string de 1D/1W/1M pasaban CRUDOS → comparaciones/Math.max
// número-vs-string en NaN o silencio (nada pintado en diario+). Contrato
// externo intacto: null para lo no interpretable, segundos UTC si no.
function cleanTime(t) {
  return toUtcSeconds(t)
}

function cleanValue(v) {
  if (v == null || Number.isNaN(v) || !Number.isFinite(v)) return null
  return v
}

function sortByTimeDesc(arr, key = 'time') {
  return [...arr].sort((a, b) => (cleanTime(b[key]) ?? 0) - (cleanTime(a[key]) ?? 0))
}

function normalizeDirection(dir) {
  const d = String(dir || '').toLowerCase()
  if (d === 'bull' || d === 'bisi' || d === 'long' || d === 'buy') return 'bull'
  if (d === 'bear' || d === 'sibi' || d === 'short' || d === 'sell') return 'bear'
  return null
}

// ─── Datos externos (inyectados desde ChartPage / ChartPublicPage) ────────────

let externalData = null
let externalVersion = 0

export function setMitigationBlockQuantumData(data) {
  externalData = data
  externalVersion++
}

export function getExternalData() {
  return externalData
}

export function getExternalVersion() {
  return externalVersion
}

// ─── Configuración por defecto ──────────────────────────────────────────────────

export const defaultConfig = {
  _version: 1,

  // ── Visibilidad ──────────────────────────────────────────────────────────
  showBullMB: true,
  showBearMB: true,
  showLabels: true,

  // ── Lógica ─────────────────────────────────────────────────────────────────
  swingLength: 5,
  mitigation: 'wick', // 'wick' | 'close'
  maxVisibleZones: 5, // Máx. bloques activos por lado (1–20)
  showMitigated: true, // Mostrar zonas mitigadas (atenuadas)
  showInvalidated: false,

  // ── Score / ranking ────────────────────────────────────────────────────────
  rankEmphasis: true, // Énfasis visual por ranking (solo activas del TF propio)
  rankTopN: 5,
  weightSpeed: 0.40,
  weightVolume: 0.35,
  weightHtf: 0.25,
  speedHorizon: 50,
  volumeLookback: 20,
  htfScoreTimeframe: '1d', // TF de referencia del rango premium/discount del score
  htfScoreLookback: 20,

  // ── MTF ────────────────────────────────────────────────────────────────────
  htfTimeframes: [], // MTF: TFs adicionales activos (solo mayores que el del chart, máx. 2)

  // ── Estilo ─────────────────────────────────────────────────────────────────
  fillOpacity: 0.14,
  borderWidth: 1.5,

  // ── Paleta Quantum ─────────────────────────────────────────────────────────
  colorBull: '#1B5E20',
  colorBear: '#B71C1C',

  // ── Dashboard ────────────────────────────────────────────────────────────
  showDashboard: true,
  dashboardMinimized: false,
  badgePosition: { x: 8, y: 8 },
  panelSections: {},
}

// ─── detect() ─────────────────────────────────────────────────────────────────

export function detect(candles, config) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!externalData) {
    const t1 = typeof performance !== 'undefined' ? performance.now() : 0
    debugLog(`[mitigation_block_quantum] detect empty: ${(t1 - t0).toFixed(2)}ms`)
    return null
  }
  const result = {
    ...externalData,
    _dataVersion: externalVersion,
  }
  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  debugLog(`[mitigation_block_quantum] detect from external: ${(t1 - t0).toFixed(2)}ms`)
  return result
}

// ─── render() ─────────────────────────────────────────────────────────────────

export function render(chart, candleSeries, result, config, api) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!result) return

  const cfg = { ...defaultConfig, ...(config || {}) }

  const allCandles = candleSeries && candleSeries.data ? candleSeries.data() : []
  // Ventana de DATOS cargada — fija e independiente del viewport. La detección
  // corre sobre ella (mismo from/to que el chart) y el viewport solo clipea
  // visualmente: una zona pintada jamás aparece/desaparece al hacer pan.
  // Reglas de reconciliación:
  // - Toda zona (activa, mitigada o invalidada) se pinta si su intervalo
  //   intersecta la ventana cargada; si no, se descarta de datos y de badge.
  // - Origen anterior a la primera vela → borde izquierdo clipeado a ella.
  // - Activas: el backend devuelve end = última vela cargada (estructura
  //   viva — llega al presente) — se respeta tal cual.
  const firstCandleTime = allCandles.length > 0 ? cleanTime(allCandles[0].time) : null
  const lastCandleTime = allCandles.length > 0 ? cleanTime(allCandles[allCandles.length - 1].time) : null

  const labelsByTime = {}
  const addLabel = (opts) => {
    const t = opts?.time
    if (t == null || Number.isNaN(t)) return
    if (!labelsByTime[t]) labelsByTime[t] = []
    labelsByTime[t].push(opts)
  }

  const renderedZones = []
  const renderedLabels = []

  // ── Mitigation Blocks ────────────────────────────────────────────────────────
  const showMitigated = cfg.showMitigated !== false
  const showInvalidated = cfg.showInvalidated === true
  const rankEmphasis = cfg.rankEmphasis !== false
  const rankTopN = Math.max(1, Number(cfg.rankTopN) || 5)

  const processMbList = (mbs, state) => {
    const out = []
    for (const mb of mbs || []) {
      const start = cleanTime(mb.start_time)
      const top = cleanValue(mb.top)
      const bottom = cleanValue(mb.bottom)
      if (start == null || top == null || bottom == null) continue

      const direction = normalizeDirection(mb.direction)
      if (direction === 'bull' && !cfg.showBullMB) continue
      if (direction === 'bear' && !cfg.showBearMB) continue
      if (!direction) continue

      const end = cleanTime(mb.end_time)
      if (end == null || end < start) continue

      // Reconciliación con la ventana de datos (ver comentario superior):
      // intersecta o se descarta; borde izquierdo clipeado a la primera vela.
      if (firstCandleTime != null && end < firstCandleTime) continue
      if (lastCandleTime != null && start > lastCandleTime) continue
      const paintStart = firstCandleTime != null ? Math.max(start, firstCandleTime) : start
      const paintEnd = lastCandleTime != null ? Math.min(end, lastCandleTime) : end
      if (paintEnd < paintStart) continue

      // MTF: las zonas de timeframes adicionales vienen marcadas con `_mtf`
      // por el merge de la página (ver utils/mtf.js). Se distinguen por
      // etiqueta con sufijo de TF (`MB+ · 4H`) y borde dotted — tres texturas,
      // tres significados: sólido = TF propio, dashed = invalidada, dotted = HTF.
      const mtf = mb._mtf && tfSeconds(mb._mtf) != null ? mb._mtf : null
      const color = direction === 'bull' ? cfg.colorBull : cfg.colorBear

      // Ranking (solo activas del TF propio con rank asignado por el backend):
      // top-N → intensidad decreciente por rango y etiqueta `#r · score`; el
      // resto de activas propias → atenuadas con etiqueta plana. Con el toggle
      // OFF todas las activas llevan el mismo estilo plano.
      const rank = Number.isFinite(mb.rank) ? mb.rank : null
      const score = Number.isFinite(mb.score) ? mb.score : null
      const ranked = state === 'active' && !mtf && rankEmphasis && rank != null && rank <= rankTopN

      let label = (direction === 'bull' ? 'MB+' : 'MB-')
      if (ranked) {
        label = `#${rank}` + (score != null ? ` · ${score.toFixed(2)}` : '')
      }
      label += mtf ? mtfSuffix(mtf) : ''

      let alpha
      let borderWidth
      let borderStyle
      if (state === 'invalidated') {
        alpha = Math.max(0.03, (cfg.fillOpacity || 0.14) * 0.45)
        borderWidth = cfg.borderWidth
        borderStyle = 'dashed'
      } else if (state === 'mitigated') {
        // Atenuada pero legible sobre fondo claro.
        alpha = Math.max(0.10, (cfg.fillOpacity || 0.14) * 0.75)
        borderWidth = 1.0
        borderStyle = 'solid'
      } else {
        alpha = cfg.fillOpacity
        borderWidth = cfg.borderWidth
        borderStyle = 'solid'
        if (!mtf && rankEmphasis) {
          if (ranked) {
            // Factor decreciente por rango: #1 al 100% de la opacidad base,
            // mínimo 0.5 para el último del top-N.
            const factor = Math.max(0.5, 1 - (rank - 1) * (0.5 / rankTopN))
            alpha = (cfg.fillOpacity || 0.14) * factor
          } else {
            // Activa propia fuera del top-N (o sin rank): atenuada.
            alpha = (cfg.fillOpacity || 0.14) * 0.6
          }
        }
      }
      // HTF manda sobre la textura de estado: el usuario debe reconocer el TF
      // de un vistazo aunque la zona esté mitigada o invalidada (el estado
      // sigue leyéndose en la opacidad).
      if (mtf) borderStyle = 'dotted'

      out.push({
        start: paintStart,
        end: paintEnd,
        top,
        bottom,
        color,
        alpha,
        borderWidth,
        borderStyle,
        label,
        type: 'MB',
        direction,
        state,
        mtf,
        rank,
        score,
        _sortTime: mb.start_time,
      })
    }
    return out
  }

  // Activas: limitadas POR LADO (maxVisibleZones bull + maxVisibleZones bear),
  // igual que anuncia el panel ("Máx. bloques por lado").
  const activeMbs = sortByTimeDesc(processMbList(result.mbs, 'active'), '_sortTime')
  const perSide = Math.max(0, cfg.maxVisibleZones ?? 5)
  const limitedActiveMbs = [
    ...activeMbs.filter((z) => z.direction === 'bull').slice(0, perSide),
    ...activeMbs.filter((z) => z.direction === 'bear').slice(0, perSide),
  ]

  // Mitigadas: visibles si el toggle está ON, sin límite de activas
  const mitigatedMbs = showMitigated
    ? sortByTimeDesc(processMbList(result.mitigated_mbs, 'mitigated'), '_sortTime')
    : []

  // Invalidadas: visibles si el toggle está ON, sin límite de activas
  const invalidatedMbs = showInvalidated
    ? sortByTimeDesc(processMbList(result.invalidated_mbs, 'invalidated'), '_sortTime')
    : []

  // Orden de pintado: las zonas HTF primero (a TF mayor, más al fondo) y las
  // del TF propio encima — la estructura grande queda detrás y la del
  // timeframe activo manda visualmente.
  const allCandidates = sortByTimeDesc(
    [...limitedActiveMbs, ...mitigatedMbs, ...invalidatedMbs],
    '_sortTime'
  )
  const htfCandidates = allCandidates
    .filter((z) => z.mtf)
    .sort((a, b) =>
      ((tfSeconds(b.mtf) ?? 0) - (tfSeconds(a.mtf) ?? 0)) ||
      ((cleanTime(b._sortTime) ?? 0) - (cleanTime(a._sortTime) ?? 0))
    )
  const ownCandidates = allCandidates.filter((z) => !z.mtf)
  const mbCandidates = [...htfCandidates, ...ownCandidates]

  // ── Pintar zonas y etiquetas ───────────────────────────────────────────────
  const paintZone = (zone) => {
    const { start, end, top, bottom, color, alpha, borderWidth, borderStyle, label, type, direction, state, mtf, rank, score } = zone

    const seriesData = [
      { time: start, upper: top, lower: bottom, fillColor: color, color, alpha, borderColor: color, borderWidth, borderStyle },
      { time: end, upper: top, lower: bottom, fillColor: color, color, alpha, borderColor: color, borderWidth, borderStyle },
    ]

    const boxSeries = api.addCustomSeries(new BoxFillSeries(), {
      color: 'rgba(0,0,0,0)',
      fillColor: 'rgba(0,0,0,0)',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      borderWidth,
      borderStyle,
      alpha,
      autoscaleInfoProvider: () => null,
    })
    if (boxSeries) boxSeries.setData(seriesData)

    if (cfg.showLabels && label) {
      addLabel({
        time: start,
        price: top,
        text: label,
        color,
        textColor: color,
        anchor: 'above',
        offset: 6,
        priority: 5,
        plaqueStyle: 'plain',
      })
      renderedLabels.push({ time: start, price: top, text: label, type, direction, state, mtf, rank, score })
    }

    renderedZones.push({ start, end, top, bottom, type, direction, color, state, alpha, borderStyle, mtf, rank, score })
  }

  for (const z of mbCandidates) paintZone(z)

  // Contadores de lo REALMENTE pintado — única fuente del badge, para que
  // badge y canvas cuadren 1:1 en cualquier config (toggles, caps…).
  // Los contadores clásicos solo cuentan el TF PROPIO (spec MTF: el badge no
  // cambia sus contadores); las zonas HTF van aparte, por TF, para la línea
  // "HTF: 4H ×3 · 1D ×1".
  const ownZones = renderedZones.filter((z) => !z.mtf)
  const htfCounts = {}
  for (const z of renderedZones) {
    if (z.mtf) htfCounts[z.mtf] = (htfCounts[z.mtf] || 0) + 1
  }
  const paintedCounts = {
    active_bull: ownZones.filter((z) => z.state === 'active' && z.direction === 'bull').length,
    active_bear: ownZones.filter((z) => z.state === 'active' && z.direction === 'bear').length,
    mitigated: ownZones.filter((z) => z.state === 'mitigated').length,
    invalidated: ownZones.filter((z) => z.state === 'invalidated').length,
    total: ownZones.length,
    htf: htfCounts,
  }
  // El objeto result viaja por referencia hasta indResults (setIndResults se
  // llama tras el render en el efecto del registro), así que el badge lo ve.
  result._paintedCounts = paintedCounts

  // ── Etiquetas de texto plano en canvas (una sola capa, frame a frame) ──────
  if (Object.keys(labelsByTime).length > 0) {
    const labelSeries = api.addCustomSeries(new LabelOverlaySeries(), {
      color: 'transparent',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      autoscaleInfoProvider: () => null,
      priceScaleId: 'right',
      plaqueStyle: 'plain',
      font: '10px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif',
    })
    if (labelSeries) {
      const labelData = Object.keys(labelsByTime)
        .map(t => ({ time: Number(t), labels: labelsByTime[t] }))
        .sort((a, b) => a.time - b.time)
      labelSeries.setData(labelData)
    }
  }

  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  debugLog(`[mitigation_block_quantum] render done: ${(t1 - t0).toFixed(2)}ms`)

  // Exponer datos de render para verificación programática externa (Playwright).
  if (typeof window !== 'undefined') {
    window.__mitigationBlockRender = {
      zones: renderedZones,
      labels: renderedLabels,
      counts: paintedCounts,
      timestamp: Date.now(),
    }
  }
}

export default {
  id: 'mitigation_block_quantum',
  name: 'Mitigation Block Quantum',
  version: '1.0.0',
  // Nada de este render depende del viewport: las primitivas son nativas del
  // chart y se desplazan con el pan. Opt-out del repintado por panTick para
  // que las zonas no parpadeen al arrastrar.
  renderOnPan: false,
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
}
